import { cn } from "@moe/priv/utils";
import { createContext, MutableRefObject, useContext, useState } from "react";

export const TAG_DELIMITER = "#";

interface TabBoxContextProps {
  suggestions: string[];
  setSuggestions: (suggestions: string[]) => void;
  idx: number;
  setIDX: (index: number | ((prev: number) => number)) => void;
  value: string;
  onChange: (value: string) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  mode: "text" | "combo";
  onTagSelect?: (tag: string) => void;
  emptyMessage?: string;
  inputRef: MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null>;
}
const TagBoxContext = createContext<TabBoxContextProps | undefined>(undefined);

export const useTagBox = () => {
  const context = useContext(TagBoxContext);
  if (!context) throw new Error("useTagBox must be used within TagBoxProvider");
  return context;
};

interface TagBoxProps {
  /**
   * "text" mode is meant for text input fields like searchbar, textarea, input, etc.
   * In text mode, tags are only suggested when we type `#` first.
   * On enter / click, we autocomplete the tag and update the value of the input field.
   *
   *
   * "combo" mode is meant for creating comboboxes, where
   * In combo mode, we autocomplete the tag and immediately closes the TagBox
   */
  mode?: "text" | "combo";
  children: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onTagSelect?: (tag: string) => void;
  className?: string;
}

/**
 * A component that allows users to enter tags and receive autocompletable suggestions.
 * Meant to be used in a compositional pattern, similar to Shadcn components.
 *
 * @example
 * <TagBox value={value} onChange={setValue} mode="combo">
 *   <TagInput placeholder="Enter tags..." />
 *   <TagList className="border-none" />
 * </TagBox>
 */
export const TagBox = ({ children, value, onChange, onTagSelect, mode = "text", className }: TagBoxProps) => {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [idx, setIDX] = useState(0);
  const [open, setOpen] = useState(false);

  /**
   * On blur, clear all suggestions.
   */
  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    // Ignore blur event if a child element is focused
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setSuggestions([]);
  };

  return (
    <TagBoxContext.Provider
      value={{
        suggestions,
        setSuggestions,
        idx,
        setIDX,
        value,
        onChange,
        onTagSelect,
        open,
        setOpen,
        mode,
        inputRef: { current: null }
      }}
    >
      <div className={cn("flex w-full flex-col gap-1", className)} onBlur={handleBlur}>
        {children}
      </div>
    </TagBoxContext.Provider>
  );
};
